import { useEffect, useRef } from 'react';

import { Organization } from 'types';

import {
    CTAButton1,
    SecondaryButton
} from '../../_shared/buttons';
import FormInputTextWithLabelSpecial from './FormInputTextWithLabelSpecial';
import PasswordRequirements from './PasswordRequirements';
import SlideInItems from './SlideInItems';

import classes from './index.module.scss';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

type SignUpInputsProps = {
    email: string;
    // TODO: type error
    error: any;
    inputsRef: {
        current: HTMLDivElement | null;
    };
    joinCode: string;
    lastName: string;
    loading: boolean;
    name: string;
    orgList: Organization[];
    orgName: string;
    password: string;
    passwordConfirmation: string;
    passwordIsValid: (password: string) => boolean;
    phoneNumber: string;
    setEmail: (email: string) => void;
    setJoinCode: (joinCode: string) => void;
    setLastName: (lastName: string) => void;
    setName: (name: string) => void;
    setOrgId: (id: null | number) => void;
    setOrgName: (name: string) => void;
    setPassword: (password: string) => void;
    setPhoneNumber: (phoneNumber: string) => void;
    setStage: (stage: string) => void;
};

const SignUpInputs = ({
    email,
    error,
    inputsRef,
    joinCode,
    lastName,
    loading,
    name,
    orgList,
    orgName,
    password,
    passwordConfirmation,
    passwordIsValid,
    phoneNumber,
    setEmail,
    setJoinCode,
    setLastName,
    setName,
    setOrgId,
    setOrgName,
    setPassword,
    setPhoneNumber,
    setStage
}: SignUpInputsProps) => {
    // have to track first mount to only scroll on mount
    const justMounted = useRef(true);

    // this ref is used to scroll email input into view
    // if theres an error with email
    const emailInputRef = useRef<HTMLInputElement | null>(null);

    // if an error while signup scroll it into view
    // only real error should be email, since
    // client validates form before submission
    useEffect(() => {
        if (emailInputRef.current && error)
            emailInputRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [error]);

    const confirmPasswordRef = useRef<HTMLObjectElement | null>(null);
    // validation for password confirmation
    useEffect(() => {
        if (!confirmPasswordRef.current) return;
        if (password !== passwordConfirmation)
            return confirmPasswordRef.current.setCustomValidity(
                "Passwords don't match"
            );
        confirmPasswordRef.current.setCustomValidity('');
    }, [password, passwordConfirmation]);

    const formatNumber = (phoneInput: string) => {
        const phoneLength = phoneInput.length;

        if (phoneLength < 4) return phoneInput;

        if (phoneLength < 7) {
            return `(${phoneInput.slice(0, 3)}) ${phoneInput.slice(3)}`;
        }

        return `(${phoneInput.slice(0, 3)}) ${phoneInput.slice(
            3,
            6
        )}-${phoneInput.slice(6, 10)}`;
    };

    const slideInItemOnRest = () => {
        // if scrolled once, then don't scroll again
        if (inputsRef.current && justMounted.current) {
            justMounted.current = false;
            inputsRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    return (
        <div className={classes.formInputsDiv} ref={inputsRef}>
            <FormInputTextWithLabelSpecial
                label="Email"
                type="email"
                id="email"
                required
                autoComplete="email"
                value={email}
                error={error}
                inputRef={emailInputRef}
                aria-label="Enter your email"
                aria-required="true"
                aria-invalid={!!error}
                aria-describedby={error && error.errors ? 'signup-email-error' : undefined}
                onChange={(e) => setEmail(e.target.value)}
            />
            {error && error.errors && (
                <div id="signup-email-error" role="alert" aria-live="polite">
                    {error.errors}
                </div>
            )}

            <FormInputTextWithLabelSpecial
                label="Password"
                type="password"
                id="password"
                required
                autoComplete="new-password"
                minLength={8}
                value={password}
                aria-label="Enter your password"
                aria-required="true"
                aria-describedby="passwordRequirements"
                onChange={(e) => setPassword(e.target.value)}
            />
            <SlideInItems
                config={{
                    onRest: slideInItemOnRest
                }}
            >
                <div>
                    <label
                        htmlFor="passwordRequirements"
                        style={{ fontSize: '14px' }}
                    >
                        Password must include:
                    </label>
                    <PasswordRequirements password={password} />
                </div>

                <div className={classes.formSectionTitle}>Account Details</div>

                <div>
                    <label htmlFor="name">First Name</label>
                    <div>
                        <input
                            type="text"
                            name="name"
                            id="name"
                            placeholder="Jane"
                            className={classes.formInput}
                            required
                            value={name}
                            aria-label="Enter your first name"
                            aria-required="true"
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="lastName">Last Name</label>
                    <div>
                        <input
                            type="text"
                            name="lastName"
                            id="lastName"
                            placeholder="Doe"
                            className={classes.formInput}
                            required
                            value={lastName}
                            aria-label="Enter your last name"
                            aria-required="true"
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="phoneNumber">10 digit phone number</label>
                    <div>
                        <input
                            type="tel"
                            name="phoneNumber"
                            id="phoneNumber"
                            placeholder="(123) 456-7890"
                            className={classes.formInput}
                            minLength={14}
                            maxLength={14}
                            required
                            value={formatNumber(phoneNumber)}
                            aria-label="Enter your phone number"
                            aria-required="true"
                            onChange={(e) =>
                                setPhoneNumber(
                                    e.target.value.replace(/[^0-9]/g, '')
                                )
                            }
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="orgName" className={classes.formLabel}>
                        Organization
                    </label>
                    <div>
                        <Autocomplete
                            id="orgName"
                            options={orgList}
                            disableClearable={true}
                            componentsProps={{
                                popper: {
                                    placement: 'top-start',
                                    modifiers: [
                                        {
                                            name: 'flip',
                                            enabled: true,
                                            options: {
                                                fallbackPlacements: ['top-start']
                                            }
                                        }
                                    ]
                                }
                            }}
                            value={
                                orgList.find((org) => org.name === orgName)
                            }
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            getOptionLabel={(option) => option.name}
                            onChange={(_, selectedOrg) => {
                                if (!selectedOrg) {
                                    setOrgName('');
                                    setOrgId(null);
                                    return;
                                }
                                setOrgName(selectedOrg.name);
                                setOrgId(selectedOrg.id);
                            }}
                            renderOption={(props, option) => (
                                <li
                                    {...props}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <img
                                        src={option.logo}
                                        alt="logo"
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '50%',
                                            marginRight: '12px'
                                        }}
                                    />
                                    {option.name}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="orgName"
                                    required
                                    aria-required="true"
                                    aria-label="Select organization"
                                    placeholder="Click to select organization"
                                    sx={{
                                        marginTop: '5px',
                                        '& .MuiOutlinedInput-root': {
                                            minHeight: '45px',
                                            backgroundColor: 'white',
                                            borderRadius: '10px',
                                            '& fieldset': {
                                                border: 'none'
                                            }
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '0.55em 1.5em',
                                            fontSize: '1rem'
                                        }
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
                {orgName.length && orgName ? (
                    <div>
                        <label htmlFor="joinCode" className={classes.formLabel}>
                            Org verification code
                        </label>
                        <div>
                            <input
                                type="text"
                                name="joinCode"
                                id="joinCode"
                                placeholder="Optional code"
                                className={classes.formInput}
                                value={joinCode}
                                aria-label="Organization verification code (optional)"
                                onChange={(e) => setJoinCode(e.target.value)}
                            />
                        </div>
                    </div>
                ) : null}
            </SlideInItems>
            <div
                style={{
                    textAlign: 'center',
                    marginTop: 40
                }}
            >
                <CTAButton1
                    invert
                    type="submit"
                    aria-label={loading ? 'Creating account' : 'Create account'}
                    aria-disabled={passwordIsValid(password) && orgName ? false : true}
                    style={{
                        paddingBlock: '0.6em',
                        outline: 'none',
                        ...(passwordIsValid(password) && orgName
                            ? {
                                  backgroundColor: '#7378E8',
                                  color: '#FFFFFF'
                              }
                            : { cursor: 'not-allowed' })
                    }}
                    disabled={
                        passwordIsValid(password) && orgName ? false : true
                    }
                >
                    {loading ? 'Creating Account' : 'Create Account'}
                </CTAButton1>
                <div
                    style={{
                        marginTop: 5,
                        paddingBottom: 40
                    }}
                >
                    or{' '}
                    <SecondaryButton
                        light
                        aria-label="Go back to sign in"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setStage('inputs');
                        }}
                    >
                        Go back to sign in
                    </SecondaryButton>
                </div>
            </div>
        </div>
    );
};

export default SignUpInputs;
